import clsx from 'clsx';
import type { ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { useCustomerMe, type CustomerMe } from '../lib/auth';

const svg = (d: ReactNode) => (
  <svg aria-hidden viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {d}
  </svg>
);
const ICONS = {
  home: svg(<path d="M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z" />),
  scan: svg(
    <>
      <path d="M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M20 16v3a1 1 0 0 1-1 1h-3M8 20H5a1 1 0 0 1-1-1v-3" />
      <path d="M8 12h8" />
    </>,
  ),
  wallet: svg(
    <>
      <rect x="3" y="6" width="18" height="14" rx="2" />
      <path d="M3 10h18M16 15h2" />
    </>,
  ),
  bills: svg(<path d="M6 3h12v18l-3-2-3 2-3-2-3 2zM9 8h6M9 12h6M9 16h3" />),
  rewards: svg(<path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z" />),
  offers: svg(<path d="M20 12l-8 8-9-9V4h7zM7.5 7.5h.01" />),
  profile: svg(
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>,
  ),
};

interface Tab {
  to: string;
  label: string;
  icon: ReactNode;
  /** Paths (prefixes) that light this tab up. */
  match: string[];
  exact?: boolean;
  /** Lower ranks stay when more than five tabs would show. */
  rank: number;
  show?: (me: CustomerMe) => boolean;
  /** The scan/pay tab: drawn as a filled button, it is the app's main action. */
  primary?: boolean;
}

const tabsFor = (me: CustomerMe | null | undefined): Tab[] => {
  const points = !!me?.pointsMode;
  return [
    { to: '/app', label: 'Home', icon: ICONS.home, match: ['/app'], exact: true, rank: 0 },
    // In points mode paying is scanning the counter's QR: the tab opens the camera straight away.
    { to: points ? '/app/scan' : '/app/pay', label: points ? 'Scan' : 'Pay', icon: ICONS.scan, match: ['/app/pay', '/app/scan'], rank: 2, show: (m) => m.features.wallet, primary: true },
    { to: '/app/wallet', label: points ? 'Points' : 'Wallet', icon: ICONS.wallet, match: ['/app/wallet', '/app/mock-checkout'], rank: 3, show: (m) => m.features.wallet },
    // Rewards only while the owner runs loyalty, or while the customer still holds coupons to use: never an empty tab.
    { to: '/app/coupons', label: 'Rewards', icon: ICONS.rewards, match: ['/app/coupons'], rank: 4, show: (m) => m.features.loyalty || m.active_coupons > 0 },
    { to: '/app/bills', label: 'Bills', icon: ICONS.bills, match: ['/app/bills'], rank: 5 },
    { to: '/app/offers', label: 'Offers', icon: ICONS.offers, match: ['/app/offers'], rank: 6 },
    { to: '/app/profile', label: 'Profile', icon: ICONS.profile, match: ['/app/profile'], rank: 1 },
  ];
};

/** At most five tabs (big enough for a thumb on a 360 px phone); the rest stay one tap away on Home and Profile. */
function visibleTabs(me: CustomerMe | null | undefined) {
  const shown = tabsFor(me).filter((t) => !t.show || (me ? t.show(me) : false));
  const keep = new Set([...shown].sort((a, b) => a.rank - b.rank).slice(0, 5).map((t) => t.to));
  return shown.filter((t) => keep.has(t.to));
}

const isActive = (t: Tab, path: string) => (t.exact ? path === t.to || path === `${t.to}/` : t.match.some((m) => path === m || path.startsWith(`${m}/`)));

/**
 * Mobile-first shell for the customer PWA: a sticky header, the page, and a bottom tab bar under the thumb. Built for
 * 360–430 px phones; on a tablet or desktop it stays a centred 32rem column. Notched phones get their safe areas.
 */
export function CustomerLayout() {
  const { data: me } = useCustomerMe();
  const { pathname } = useLocation();
  const visible = visibleTabs(me);
  const side = 'pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))]';
  return (
    <div className="cust mx-auto flex min-h-dvh w-full max-w-lg flex-col overflow-x-clip bg-slate-50 sm:border-x sm:border-slate-200">
      <header className={clsx('sticky top-0 z-20 flex items-center justify-between gap-3 bg-shell pb-2 pt-[max(0.5rem,env(safe-area-inset-top))] text-white', side)}>
        <Link to="/app" className="tap min-w-0 truncate text-lg font-bold tracking-tight">
          {me?.business?.name ?? 'Carnival City'}
        </Link>
        <NavLink
          to="/app/inbox"
          className="relative inline-flex h-11 shrink-0 items-center gap-1.5 rounded-full px-3 text-sm text-slate-200 hover:bg-white/10"
          aria-label={`Inbox${me?.unread ? `, ${me.unread} unread` : ''}`}
        >
          <svg aria-hidden viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.9 1.9 0 0 0 3.4 0" />
          </svg>
          Inbox
          {!!me?.unread && <span className="absolute right-0 top-0.5 min-w-5 rounded-full bg-accent-500 px-1 text-center text-xs font-semibold tabular">{me.unread}</span>}
        </NavLink>
      </header>
      <main className={clsx('flex-1 pb-[calc(5.5rem+env(safe-area-inset-bottom))] pt-4', side)}>
        <Outlet />
      </main>
      <nav aria-label="App" className="fixed inset-x-0 bottom-0 z-30 mx-auto w-full max-w-lg border-t border-slate-200 bg-white pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] shadow-[0_-2px_10px_rgb(0_0_0/0.06)]">
        <ul className="flex">
          {visible.map((t) => {
            const active = isActive(t, pathname);
            return (
              <li key={t.label} className="min-w-0 flex-1">
                <Link
                  to={t.to}
                  aria-current={active ? 'page' : undefined}
                  className={clsx('flex h-16 flex-col items-center justify-center gap-0.5 px-1 text-xs', active ? 'font-semibold text-brand-700' : 'text-slate-500')}
                >
                  <span
                    className={clsx(
                      'flex h-8 w-14 items-center justify-center rounded-full transition-colors',
                      t.primary ? (active ? 'bg-brand-700 text-white' : 'bg-accent-500 text-white') : active && 'bg-brand-50',
                    )}
                  >
                    {t.icon}
                  </span>
                  <span className="max-w-full truncate">{t.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
